import React, { useEffect } from 'react';
import { ChildProps, compose, graphql } from 'react-apollo';
import {
  CoreMarketplaceCombinedMarketplaceSearch,
} from '../schemas/rql';
import { combinedMarketplaceSearchQuery, mapUrlParams } from './marketplace/map_params';
import {
  reverb_search_ListingsSearchRequest_Sort,
} from '@reverbdotcom/commons/src/rql';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { MarketplacePathContext } from './shared/url_helpers';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import experiments from '@reverbdotcom/commons/src/experiments';
import ListingsCollection from '@reverbdotcom/commons/src/components/listings_collection';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import I18n from 'i18n-js';
import { Paths } from '@reverbdotcom/commons/src/url_helpers';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { Location } from 'history';

interface RouterLocationProps {
  location: Location;
  params: MarketplacePathContext;
}

type IUserProps = RouterLocationProps & IUserContext;

interface IProps extends IUserProps {
  subquery: string;
  setAnyDisplayedRows: Function;
  listingsSearchCount: Number;
}

const LISTINGS_QUERY_LIMIT = 12;
const COMPONENT_NAME = 'SubqueryRow';

function CallToAction({ subquery }) {
  const clickTrackingData = {
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ClickedSubqueryRowSeeMoreLink,
    subquery,
  };

  return (
    <CoreLink
      to={Paths.marketplace.expand({ query: subquery })}
      clickEvent={clickTrackingData}
    >
      {I18n.t('cms.listingsCollection.seeMore')}
    </CoreLink>
  );
}

export function SubqueryRow(props: ChildProps<IProps, CoreMarketplaceCombinedMarketplaceSearch.Query, CoreMarketplaceCombinedMarketplaceSearch.Variables>) {
  const { subquery, setAnyDisplayedRows, listingsSearchCount } = props;

  const listings = props.data.listingsSearch?.listings;
  const count = props.data.listingsSearch?.total;

  useEffect(() => {
    setAnyDisplayedRows(props.data.listingsSearch && count !== 0 && count !== listingsSearchCount);
  }, [props.data.listingsSearch, count, listingsSearchCount]);

  const viewTrackingData = {
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ComponentView,
    subquery,
  };

  useViewTracking(
    viewTrackingData,
    (!!props.data.listingsSearch && count !== 0 && count !== listingsSearchCount),
  );

  if (count === 0 || count === listingsSearchCount) return null;

  return (
    <>
      <ListingsCollection
        componentName={COMPONENT_NAME}
        callToAction={<CallToAction subquery={subquery} />}
        listings={listings}
        title={I18n.t('discovery.marketplace.searchResults.resultsForQuery', { count, subquery })}
      />
    </>
  );
}

const withQuery = graphql<IProps, CoreMarketplaceCombinedMarketplaceSearch.Query, CoreMarketplaceCombinedMarketplaceSearch.Variables>(
  combinedMarketplaceSearchQuery,
  {
    options: (props) => {
      return {
        ssr: false,
        variables: {
          ...mapUrlParams(
            props.params,
            props.location?.query || {},
            props.user,
            {
              useExperimentalRecall: isExperimentEnabled(props.user, experiments.SEARCH_RECALL_IMPROVEMENTS),
            },
            0,
            LISTINGS_QUERY_LIMIT,
            props.subquery,
          ),
          boostByBumpRate: false,
          bumpLimit: 0,
          bumpOffset: 0,
          sort: reverb_search_ListingsSearchRequest_Sort.NONE,
        },
      };
    },
  },
);

export default compose(
  withUserContext,
  withQuery,
)(SubqueryRow);
